import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { track } from "../lib/analytics";
import { toast } from "sonner";

/**
 * 1-question post-drop survey (NPS 0–10 + Attend Tomorrow?).
 * Auto-appears when a live enters `ended` state. Dedupes per (user, live_id)
 * via localStorage so it never re-prompts the same user for the same drop.
 */
export default function PostDropSurvey({ liveId, liveTitle, onClose }) {
  const { user } = useAuth();
  const stashKey = `dsl_survey_${liveId}_${user?.user_id || "anon"}`;
  const [nps, setNps] = useState(null);
  const [attend, setAttend] = useState(null);
  const [step, setStep] = useState("ask");    // ask | thanks
  const [busy, setBusy] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(stashKey) === "done") return;
    } catch (_e) { /* noop */ }
    // Delay 1.2s so it doesn't spring the second the state flips
    const t = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(t);
  }, [stashKey]);

  const close = (persist = false) => {
    if (persist) {
      try { localStorage.setItem(stashKey, "done"); } catch (_e) { /* noop */ }
    }
    setVisible(false);
    if (onClose) onClose();
  };

  const submit = async () => {
    if (nps === null && attend === null) return;
    setBusy(true);
    try {
      await api.post("/survey/drop", { live_id: liveId, nps, attend_tomorrow: attend });
      track("survey_submit", { live_id: liveId, nps, attend_tomorrow: attend });
      setStep("thanks");
      try { localStorage.setItem(stashKey, "done"); } catch (_e) { /* noop */ }
      setTimeout(() => close(true), 1800);
    } catch (_err) {
      toast.error("Couldn't send your response. Try again in a moment.");
      // Keep the modal open (do not persist localStorage) so the user can retry.
    } finally { setBusy(false); }
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-end md:items-center justify-center bg-black/60 backdrop-blur-sm px-4 pb-4 md:pb-0" data-testid="drop-survey">
      <div className="w-full max-w-md bg-[#FFFFFF] border-2 border-[#161616] shadow-[6px_6px_0_#264733] p-6 md:p-7">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="brand-tag">// Post-Drop · 30-second survey</div>
            <div className="font-display text-xl md:text-2xl uppercase tracking-tight mt-1 truncate" data-testid="survey-live-title">
              {liveTitle || "Tonight's Drop"}
            </div>
          </div>
          <button
            onClick={() => close(true)}
            className="w-8 h-8 border border-[#E5E1D8] hover:border-[#FF6A00] hover:text-[#FF6A00] font-mono-ds"
            data-testid="survey-close"
            aria-label="Close survey"
          >×</button>
        </div>

        {step === "ask" ? (
          <>
            {/* Q1 — NPS */}
            <div className="mt-5">
              <div className="font-mono-ds text-[11px] uppercase tracking-[0.24em] text-[#161616] mb-2">
                How likely are you to recommend Drop Shop Live to a friend?
              </div>
              <div className="grid grid-cols-11 gap-1" data-testid="nps-scale">
                {Array.from({ length: 11 }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setNps(i)}
                    className={`h-9 border font-mono-ds text-[11px] transition ${
                      nps === i
                        ? (i >= 9 ? "border-[#264733] bg-[#264733] text-[#B9E641] font-bold" :
                           i >= 7 ? "border-[#264733] bg-[#B9E641] text-[#161616] font-bold" :
                                    "border-[#FF6A00] bg-[#FF6A00] text-[#161616] font-bold")
                        : "border-[#E5E1D8] bg-[#FFFFFF] text-[#6E6E6E] hover:border-[#161616]"
                    }`}
                    data-testid={`nps-${i}`}
                  >{i}</button>
                ))}
              </div>
              <div className="flex justify-between font-mono-ds text-[9px] uppercase tracking-[0.24em] text-[#6E6E6E] mt-1.5">
                <span>Not likely</span><span>Very likely</span>
              </div>
            </div>

            {/* Q2 — Attend tomorrow */}
            <div className="mt-6">
              <div className="font-mono-ds text-[11px] uppercase tracking-[0.24em] text-[#161616] mb-2">
                Will you attend tomorrow&rsquo;s drop?
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setAttend(true)}
                  className={`h-11 border font-mono-ds text-[11px] uppercase tracking-[0.24em] ${
                    attend === true ? "border-[#264733] bg-[#B9E641] text-[#161616] font-bold" : "border-[#E5E1D8] bg-[#FFFFFF] text-[#6E6E6E] hover:border-[#161616]"
                  }`}
                  data-testid="attend-yes"
                >
                  ✓ Yes, I&rsquo;ll be there
                </button>
                <button
                  onClick={() => setAttend(false)}
                  className={`h-11 border font-mono-ds text-[11px] uppercase tracking-[0.24em] ${
                    attend === false ? "border-[#FF6A00] bg-[#FF6A00]/20 text-[#FF6A00] font-bold" : "border-[#E5E1D8] bg-[#FFFFFF] text-[#6E6E6E] hover:border-[#161616]"
                  }`}
                  data-testid="attend-no"
                >
                  Maybe next time
                </button>
              </div>
            </div>

            <button
              onClick={submit}
              disabled={busy || (nps === null && attend === null)}
              className="btn-urgent mt-6 w-full h-12 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="survey-submit"
            >
              {busy ? "Sending…" : "Submit →"}
            </button>
            <button
              onClick={() => close(true)}
              className="mt-2 w-full text-center font-mono-ds text-[10px] uppercase tracking-[0.24em] text-[#6E6E6E] hover:text-[#FF6A00] py-2"
              data-testid="survey-skip"
            >
              Skip this time
            </button>
          </>
        ) : (
          <div className="mt-6 text-center py-6" data-testid="survey-thanks">
            <div className="font-display text-4xl leading-none">✓</div>
            <div className="brand-tag mt-3">// Thanks</div>
            <div className="font-display text-xl uppercase tracking-tight mt-1">See you at the next drop.</div>
          </div>
        )}
      </div>
    </div>
  );
}
